import { useState } from "react";
import { toast } from "sonner";
import { FileSignature, FileDown, PenLine, BellRing } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { SignaturePad } from "../SignaturePad";
import { Modal, F, inp, btnPrimary, btnGhost, iconBtn, frDate, useList, StatusBadge, Table, Empty } from "./common";
import { openPdf } from "./OrderDialogs";

const SignDialog = ({ h, role, onClose, onDone }) => {
  const [name, setName] = useState(role === "recipient" && h.kind === "agent" ? h.target_name : "");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("role", role); fd.append("name", name); fd.append("signature", sig, "signature.png");
      await http.post(`/equipment/handovers/${h.id}/sign`, fd); toast.success("Signature enregistrée"); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={role === "issuer" ? "Signature de la société" : h.kind === "agent" ? "Signature de l'agent" : "Signature du responsable du site"} onClose={onClose} testid="handover-sign">
      <F label="Nom et prénom du signataire"><input className={inp} value={name} onChange={(e) => setName(e.target.value)} data-testid="handover-sign-name" /></F>
      <SignaturePad testid="handover-signature" onChange={setSig} />
      <div className="flex justify-end gap-2"><button className={btnGhost} onClick={onClose}>Annuler</button>
        <button className={btnPrimary} disabled={busy || !sig || name.trim().length < 2} onClick={go} data-testid="handover-sign-save">Signer</button></div>
    </Modal>
  );
};

const Reminders = ({ h, onDone }) => {
  const [busy, setBusy] = useState(false);
  const rem = h.reminders || [];
  const pending = !h.signatures?.recipient;
  const remind = async () => {
    setBusy(true);
    try { await http.post(`/equipment/handovers/${h.id}/remind`); toast.success("Rappel envoyé à l'agent"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 text-[11px] text-slate-500" data-testid={`handover-reminders-${h.id}`}>
      {rem.length > 0 && <details><summary className="cursor-pointer" data-testid={`handover-reminders-count-${h.id}`}>{rem.length} rappel{rem.length > 1 ? "s" : ""} · dernier le {frDate(rem[rem.length - 1].at)}</summary>
        <ul className="mt-1 space-y-0.5">{rem.map((r, i) => <li key={i}>{new Date(r.at).toLocaleString("fr-FR")} — {r.by}{r.push ? " · notifié sur téléphone" : ""}</li>)}</ul></details>}
      {pending && <button type="button" disabled={busy} onClick={remind} className="inline-flex items-center gap-1 text-sky-700 font-semibold hover:underline disabled:opacity-50" data-testid={`handover-remind-${h.id}`}><BellRing className="h-3 w-3" />Relancer</button>}
    </div>
  );
};

export const HandoverList = ({ kind, targetId }) => {
  const [rows, load] = useList("/equipment/handovers", { kind, target_id: targetId });
  const [sign, setSign] = useState(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (busy) return;
    setBusy(true);
    try { const r = await http.post("/equipment/handovers", { kind, target_id: targetId }); toast.success("Fiche créée avec la liste actuelle"); load(); setSign({ h: r.data, role: "issuer" }); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const who = (h, role) => h.signatures?.[role];
  return (
    <div className="space-y-2" data-testid={`handover-list-${kind}`}>
      <div className="flex items-center gap-2">
        <p className="text-xs text-slate-500 flex-1">{kind === "agent" ? "L'agent peut signer ici sur l'écran ou depuis son espace agent (onglet Accueil → Mes équipements)." : "Le responsable du site signe à l'écran (téléphone ou tablette)."}</p>
        <button type="button" className={btnPrimary} disabled={busy} onClick={create} data-testid={`handover-create-${kind}`}><FileSignature className="h-4 w-4" />Fiche de remise / inventaire</button>
      </div>
      <Table head={["Fiche du", "Articles", "Société", kind === "agent" ? "Agent" : "Site", "Statut", ""]} testid={`handover-table-${kind}`}>
        {rows.length === 0 && <tr><td colSpan={6}><Empty>Aucune fiche de remise</Empty></td></tr>}
        {rows.map((h) => (
          <tr key={h.id} data-testid={`handover-row-${h.id}`}>
            <td className="px-3 py-2 whitespace-nowrap">{frDate(h.created_at)}</td>
            <td className="px-3 py-2">{h.items.length}</td>
            {["issuer", "recipient"].map((r) => (
              <td key={r} className="px-3 py-2 text-xs">{who(h, r) ? <span className="text-emerald-700">✓ {who(h, r).name}<div className="text-slate-500">{frDate(who(h, r).at)} · {who(h, r).via}</div></span>
                : <button type="button" className="inline-flex items-center gap-1 text-sky-700 font-semibold hover:underline" onClick={() => setSign({ h, role: r })} data-testid={`handover-sign-${r}-${h.id}`}><PenLine className="h-3.5 w-3.5" />Signer</button>}</td>
            ))}
            <td className="px-3 py-2"><StatusBadge s={h.status === "signed" ? "covered" : "pending"} />
              {kind === "agent" && <Reminders h={h} onDone={load} />}</td>
            <td className="px-3 py-2 text-right"><button type="button" className={iconBtn} title="PDF" onClick={() => openPdf(`/equipment/handovers/${h.id}/pdf`)} data-testid={`handover-pdf-${h.id}`}><FileDown className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </Table>
      {sign && <SignDialog h={sign.h} role={sign.role} onClose={() => setSign(null)} onDone={load} />}
    </div>
  );
};
